import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../components/Icon.jsx';
import Modal from '../../components/Modal.jsx';
import { useT, useLang, t as translate } from '../../lib/i18n.jsx';
import { useAsync, initials } from '../../lib/hooks.js';
import { formatDate, daysUntil } from '../../lib/format.js';
import { get, post, patch, del } from '../../lib/api.js';
import { toast } from '../../lib/toast.jsx';
import { ConfirmDialog, FormField, InlineError } from '../hosting/detailUi.jsx';
import { activateMail, activateMailError } from '../email/mailDomains.js';

// API: GET/POST /dns/domains/{id}/records, PATCH/DELETE /dns/records/{id},
// GET/POST /dns/domains/{id}/redirects, PATCH/DELETE /dns/redirects/{id},
// PATCH /dns/domains/{id}/nameservers, GET/PATCH /dns/domains/{id}/dnssec.
// Write endpoints take JSON bodies; empty optional fields are left out.
function cleanBody(params) {
  return Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''));
}

const recordApi = {
  create: (domainId, r) => {
    const p = { record_type: r.type, name: r.name, value: r.value, ttl: r.ttl, priority: r.priority };
    return post(`/dns/domains/${domainId}/records`, cleanBody(p));
  },
  update: (recordId, r) => {
    const p = { name: r.name, value: r.value, ttl: r.ttl, priority: r.priority };
    return patch(`/dns/records/${recordId}`, cleanBody(p));
  },
  remove: (recordId) => del(`/dns/records/${recordId}`),
};

const redirectApi = {
  create: (domainId, r) => {
    const p = { source_subdomain: r.source, target_url: r.target, redirect_type: r.code, include_path: r.includePath, force_https: true };
    return post(`/dns/domains/${domainId}/redirects`, cleanBody(p));
  },
  remove: (id) => del(`/dns/redirects/${id}`),
};

const RECORD_TYPES = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'SRV', 'CAA'];
const IPV4 = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;
const HOSTNAME = /^(?=.{1,253}\.?$)([a-z0-9_](?:[a-z0-9-_]{0,61}[a-z0-9_])?\.)*[a-z0-9_](?:[a-z0-9-_]{0,61}[a-z0-9_])?\.?$/i;

function recType(r) {
  return String(r.record_type || r.type || '').toUpperCase();
}

export default function DomainDetail({ domain, onBack, reload, initialTab }) {
  const t = useT();
  const { lang } = useLang();
  const validTabs = ['overview', 'dns', 'email', 'forward', 'settings'];
  const [tab, setTab] = useState(validTabs.includes(initialTab) ? initialTab : 'overview');
  const records = useAsync(() => get(`/dns/domains/${domain.id}/records`), [domain.id]);
  const days = daysUntil(domain.expires_at);

  return (
    <main className="page" data-screen-label={`Domein: ${domain.domain_name}`}>
      <button className="back-link" onClick={onBack}>
        <Icon name="arrow-left" size={14} />
        <span style={{ marginLeft: 4 }}>{t('domainDetail.allDomains')}</span>
      </button>

      <div className="detail-head" style={{ flexWrap: 'wrap' }}>
        <div className="fav-lg">{initials(domain.domain_name.split('.')[0])}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ overflowWrap: 'anywhere' }}>{domain.domain_name}</h1>
          <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className={'status-pill ' + statusClass(domain.status)}>
              <span className="dot" />
              {statusLabel(domain.status, lang)}
            </span>
            {domain.expires_at && (
              <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
                {t('dom.col.expires')}{' '}
                <span style={{ color: 'var(--ink-2)' }}>{formatDate(domain.expires_at, lang)}</span>
                {days != null && days >= 0 && days <= 60 && (
                  <span style={{ color: 'var(--warn)' }}>{t(days === 1 ? 'domainDetail.expiresIn.one' : 'domainDetail.expiresIn.other', { n: days })}</span>
                )}
              </span>
            )}
            <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
              · {t(domain.auto_renew ? 'domainDetail.autoRenewState.on' : 'domainDetail.autoRenewState.off')}
            </span>
          </div>
        </div>
      </div>

      <div className="tabs" role="tablist">
        {[
          ['overview', 'dd.tab.overview'],
          ['dns', 'dd.tab.dns'],
          ['email', 'dd.tab.email'],
          ['forward', 'dd.tab.forward'],
          ['settings', 'dd.tab.settings'],
        ].map(([k, key]) => (
          <button key={k} role="tab" aria-selected={tab === k} aria-current={tab === k ? 'page' : undefined} onClick={() => setTab(k)}>
            {t(key)}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab domain={domain} lang={lang} />}
      {tab === 'dns' && <DnsTab domain={domain} records={records} lang={lang} t={t} />}
      {tab === 'email' && <MailRoutingTab domain={domain} records={records} lang={lang} />}
      {tab === 'forward' && <ForwardingTab domain={domain} lang={lang} />}
      {tab === 'settings' && <SettingsTab domain={domain} lang={lang} reload={reload} />}
    </main>
  );
}

function statusClass(s) {
  if (s === 'active') return '';
  if (s === 'expired' || s === 'cancelled') return 'is-danger';
  return 'is-warn';
}

const DOMAIN_STATUSES = ['active', 'pending', 'transferring', 'expired', 'suspended'];

function statusLabel(s, lang) {
  if (DOMAIN_STATUSES.includes(s)) return translate(lang, `domainDetail.status.${s}`);
  return s || translate(lang, 'domainDetail.status.unknown');
}

function Kv({ k, v, mono }) {
  return (
    <div className="kv">
      <span className="k">{k}</span>
      <span className={'v ' + (mono ? 'mono' : '')} style={{ overflowWrap: 'anywhere' }}>{v}</span>
    </div>
  );
}

// Mail domain (if any) with the same name — gives account count and the mail
// server's recommended DNS records.
function useMailDomain(domainName) {
  return useMailDomainState(domainName)[0];
}

function useMailDomainState(domainName) {
  const { data: mailDomains, reload } = useAsync(() => get('/emails/domains').catch(() => []), []);
  const mailDomain = useMemo(() => (mailDomains || []).find((m) => m.domain === domainName) || null, [mailDomains, domainName]);
  return [mailDomain, reload];
}

// ─────────────────────────────────────────────────────────────
// Overview tab

function OverviewTab({ domain, lang }) {
  const t = useT();
  const navigate = useNavigate();
  const { data: hosting } = useAsync(
    () => (domain.hosting_id ? get(`/hostings/${domain.hosting_id}`).catch(() => null) : Promise.resolve(null)),
    [domain.hosting_id],
  );
  const mailDomain = useMailDomain(domain.domain_name);
  // The set the domain should be delegated to (domain → package → global), not only the last one sent.
  const nsView = useNameserverView(domain.id).data;
  const ns = nsView ? nsView.effective : (domain.nameservers || []);

  return (
    <div className="det-grid">
      <section className="card">
        <div className="card-h"><h3>{t('domainDetail.registration')}</h3></div>
        <div style={{ padding: '6px 22px 16px' }}>
          <Kv k={t('dd.registered')} v={formatDate(domain.registered_at, lang) || '—'} />
          <Kv k={t('dom.col.expires')} v={formatDate(domain.expires_at, lang) || '—'} />
          <Kv k={t('domainDetail.autoRenew')} v={t(domain.auto_renew ? 'domainDetail.on' : 'domainDetail.off')} />
          <Kv k="DNSSEC" v={t(domain.dnssec_enabled ? 'domainDetail.on' : 'domainDetail.off')} />
          <Kv
            k={t('domainDetail.nameservers')}
            v={ns.length ? <span className="mono" style={{ display: 'inline-block' }}>{ns.map((n) => <div key={n}>{n}</div>)}</span> : '—'}
          />
        </div>
      </section>

      <section className="card">
        <div className="card-h"><h3>{t('domainDetail.linkedServices')}</h3></div>
        <div style={{ padding: '16px 22px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {hosting && (
            <LinkedService
              icon="server"
              title={t('domainDetail.webHosting')}
              sub={[hosting.package?.name, hosting.server?.host].filter(Boolean).join(' · ')}
              onOpen={() => navigate(`/hosting/${hosting.id}`)}
            />
          )}
          {mailDomain && (
            <LinkedService
              icon="mail"
              title={t(mailDomain.account_count === 1 ? 'domainDetail.emailService.one' : 'domainDetail.emailService.other', { n: mailDomain.account_count })}
              sub={mailDomain.mail_server_name || ''}
              onOpen={() => navigate('/e-mail')}
            />
          )}
          {hosting?.ssl_enabled && (
            <LinkedService icon="shield" title="SSL" sub={hosting.ssl_provider || "Let's Encrypt"} />
          )}
          {!hosting && !mailDomain && (
            <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
              {t('domainDetail.notLinked')}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function LinkedService({ icon, title, sub, onOpen }) {
  const t = useT();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '36px minmax(0,1fr) auto', gap: 12, alignItems: 'center' }}>
      <div style={{ width: 36, height: 36, borderRadius: 9, background: 'var(--surface-2)', display: 'grid', placeItems: 'center', color: 'var(--ink-2)' }}>
        <Icon name={icon} size={17} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--ink)' }}>{title}</div>
        {sub && <div style={{ fontSize: 12, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)', overflowWrap: 'anywhere' }}>{sub}</div>}
      </div>
      {onOpen && (
        <button className="btn-ghost" onClick={onOpen} aria-label={t('domainDetail.openService', { name: title })}>→</button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DNS tab — simple/advanced modes

function DnsTab({ domain, records, lang, t }) {
  const [mode, setMode] = useState('simple');
  const [taskOpen, setTaskOpen] = useState(null);
  const [editing, setEditing] = useState(null); // { record } | { record: null } for new
  const list = records.data || [];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' }}>
            {mode === 'simple' ? t('domainDetail.dns.simpleTitle') : t('domainDetail.dns.allRecords')}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 3 }}>
            {mode === 'simple' ? t('domainDetail.dns.simpleSub') : t('domainDetail.dns.advancedSub')}
          </div>
        </div>
        <div className="dns-toggle" role="group" aria-label={t('domainDetail.dns.mode')}>
          <button aria-pressed={mode === 'simple'} onClick={() => setMode('simple')}>{t('domainDetail.dns.simple')}</button>
          <button aria-pressed={mode === 'advanced'} onClick={() => setMode('advanced')}>{t('domainDetail.dns.advanced')}</button>
        </div>
      </div>

      {mode === 'simple' ? (
        <div className="task-grid">
          <TaskCard icon="globe" title={t('tw.title', { domain: domain.domain_name })}
            desc={t('domainDetail.task.website.desc')}
            onClick={() => setTaskOpen('website')} />
          <TaskCard icon="mail" title={t('domainDetail.task.mail.title')}
            desc={t('domainDetail.task.mail.desc')}
            onClick={() => setTaskOpen('mail')} />
          <TaskCard icon="shield" title={t('domainDetail.task.verify.title')}
            desc={t('domainDetail.task.verify.desc')}
            onClick={() => setTaskOpen('verify')} />
          <TaskCard icon="arrow-right" title={t('domainDetail.task.forward.title')}
            desc={t('domainDetail.task.forward.desc')}
            onClick={() => setTaskOpen('forward')} />
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '14px 22px', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
              {t(list.length === 1 ? 'domainDetail.records.one' : 'domainDetail.records.other', { n: list.length })}
            </span>
            <button className="btn-primary" style={{ padding: '7px 12px', fontSize: 12.5 }} onClick={() => setEditing({ record: null })}>
              <Icon name="plus" size={13} stroke={2} />
              <span style={{ marginLeft: 4 }}>{t('domainDetail.addRecord')}</span>
            </button>
          </div>
          {records.loading && <div style={{ padding: '14px 22px', color: 'var(--ink-3)', fontSize: 13 }}>{t('common.loading')}</div>}
          {records.error && <div style={{ padding: '14px 22px' }}><InlineError error={records.error} lang={lang} /></div>}
          {!records.loading && !records.error && list.length === 0 && (
            <div className="empty-state">
              <div className="iccirc"><Icon name="settings" size={22} /></div>
              <h4>{t('domainDetail.noRecords')}</h4>
              <p>{t('domainDetail.noRecords.sub')}</p>
            </div>
          )}
          {list.length > 0 && (
            <div className="dns-tbl-head">
              <span>{t('domainDetail.col.type')}</span>
              <span>{t('domainDetail.name')}</span>
              <span>{t('rec.value')}</span>
              <span>TTL</span>
              <span />
            </div>
          )}
          {list.map((r) => (
            <div className="dns-row" key={r.id}>
              <span className={`type ${recType(r).toLowerCase()}`}>{recType(r)}</span>
              <span className="name">{r.name}</span>
              <span className="value" title={r.value}>{r.priority != null ? `${r.priority} ` : ''}{r.value}</span>
              <span style={{ color: 'var(--ink-3)' }}>{r.ttl}</span>
              <span className="actions">
                <button className="icon-btn" title={t('domainDetail.edit')} aria-label={t('domainDetail.editRecord', { type: recType(r), name: r.name })} onClick={() => setEditing({ record: r })}>
                  <Icon name="settings" size={14} />
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      {taskOpen === 'website' && <PointToWebsiteModal domain={domain} records={list} lang={lang} t={t} onClose={() => setTaskOpen(null)} onDone={records.reload} />}
      {taskOpen === 'mail' && <MailPresetModal domain={domain} records={list} lang={lang} onClose={() => setTaskOpen(null)} onDone={records.reload} />}
      {taskOpen === 'verify' && <VerifyModal domain={domain} lang={lang} onClose={() => setTaskOpen(null)} onDone={records.reload} />}
      {taskOpen === 'forward' && <RedirectModal domain={domain} lang={lang} onClose={() => setTaskOpen(null)} onDone={() => {}} />}

      {editing && (
        <RecordModal
          record={editing.record}
          domain={domain}
          lang={lang}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            records.reload();
          }}
        />
      )}
    </>
  );
}

function TaskCard({ icon, title, desc, onClick }) {
  return (
    <button className="task-card" onClick={onClick}>
      <span className="ic"><Icon name={icon} size={18} /></span>
      <span>
        <div className="ti">{title}</div>
        <div className="de">{desc}</div>
      </span>
      <span className="ar"><Icon name="arrow-right" size={16} /></span>
    </button>
  );
}

function validateRecord(type, value, priority, lang) {
  const v = value.trim();
  if (!v) return translate(lang, 'domainDetail.err.value');
  if (type === 'A' && !IPV4.test(v)) return translate(lang, 'domainDetail.err.ipv4');
  if (type === 'AAAA' && !/^[0-9a-f:]+$/i.test(v)) return translate(lang, 'domainDetail.err.ipv6');
  if ((type === 'CNAME' || type === 'MX' || type === 'NS') && !HOSTNAME.test(v)) return translate(lang, 'domainDetail.err.hostname');
  if ((type === 'MX' || type === 'SRV') && (priority === '' || Number.isNaN(Number(priority)))) return translate(lang, 'domainDetail.err.priority');
  return '';
}

// ─────────────────────────────────────────────────────────────
// Add / edit / delete a single record

function RecordModal({ record, domain, lang, onClose, onDone }) {
  const t = useT();
  const isNew = !record;
  const [type, setType] = useState(record ? recType(record) : 'A');
  const [name, setName] = useState(record?.name || '@');
  const [value, setValue] = useState(record?.value || '');
  const [ttl, setTtl] = useState(record?.ttl || 3600);
  const [priority, setPriority] = useState(record?.priority != null ? String(record.priority) : '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [touched, setTouched] = useState(false);

  const needsPrio = type === 'MX' || type === 'SRV';
  const valueError = validateRecord(type, value, needsPrio ? priority : '0', lang);
  const ttlOptions = Array.from(new Set([300, 1800, 3600, 14400, 86400, Number(ttl)])).sort((a, b) => a - b);

  const submit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!name.trim() || valueError) return;
    setBusy(true);
    setErr(null);
    const payload = { type, name: name.trim(), value: value.trim(), ttl: Number(ttl), priority: needsPrio ? Number(priority) : undefined };
    try {
      if (isNew) await recordApi.create(domain.id, payload);
      else await recordApi.update(record.id, payload);
      toast(t(isNew ? 'domainDetail.recordAdded' : 'domainDetail.recordSaved'));
      onDone();
    } catch (e2) {
      setErr(e2);
      setBusy(false);
    }
  };

  if (confirmDel) {
    return (
      <ConfirmDialog
        lang={lang}
        title={t('domainDetail.deleteRecord.title')}
        body={`${recType(record)} ${record.name} → ${record.value}`}
        cta={t('domainDetail.delete')}
        onClose={() => setConfirmDel(false)}
        onConfirm={async () => {
          await recordApi.remove(record.id);
          toast(t('domainDetail.recordDeleted'));
          onDone();
        }}
      />
    );
  }

  return (
    <Modal
      as="form"
      onSubmit={submit}
      onClose={onClose}
      maxWidth={580}
      title={t(isNew ? 'domainDetail.addDnsRecord' : 'domainDetail.editDnsRecord')}
      description={t('domainDetail.propagation')}
      footer={
        <>
          {!isNew && (
            <button type="button" className="btn-ghost" style={{ color: 'var(--danger)', marginRight: 'auto' }} onClick={() => setConfirmDel(true)}>
              {t('domainDetail.delete')}
            </button>
          )}
          <button type="button" className="btn-ghost" onClick={onClose}>{t('g.cancel')}</button>
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? t('domainDetail.working') : t('g.save')}
          </button>
        </>
      }
    >
      <div className="modal-body">
        <InlineError error={err} lang={lang} />
        <div className="field-row">
          <FormField label={t('domainDetail.col.type')} hint={!isNew ? t('domainDetail.typeFixed') : ''}>
            <select value={type} onChange={(e) => setType(e.target.value)} disabled={!isNew}>
              {RECORD_TYPES.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
            </select>
          </FormField>
          <FormField label={t('domainDetail.ttlSeconds')}>
            <select value={ttl} onChange={(e) => setTtl(Number(e.target.value))}>
              {ttlOptions.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </FormField>
        </div>
        <FormField
          label={t('domainDetail.name')}
          hint={t('domainDetail.name.hint', { domain: domain.domain_name })}
          error={touched && !name.trim() ? t('domainDetail.err.name') : ''}
        >
          <input value={name} onChange={(e) => setName(e.target.value)} className="mono" autoComplete="off" spellCheck={false} />
        </FormField>
        {needsPrio && (
          <FormField label={t('rec.prio')} hint={t('domainDetail.priority.hint')}>
            <input type="number" min="0" max="65535" value={priority} onChange={(e) => setPriority(e.target.value)} />
          </FormField>
        )}
        <FormField label={t('rec.value')} error={touched ? valueError : ''}>
          {type === 'TXT' ? (
            <textarea value={value} onChange={(e) => setValue(e.target.value)} rows={3} className="mono" style={{ fontSize: 12.5, resize: 'vertical' }} />
          ) : (
            <input value={value} onChange={(e) => setValue(e.target.value)} className="mono" autoComplete="off" spellCheck={false} />
          )}
        </FormField>
      </div>
    </Modal>
  );
}

// Replaces the records in `remove` by those in `create`. Deletes run first:
// a CNAME may not coexist with other records of the same name.
async function replaceRecords(domainId, remove, create) {
  for (const r of remove) await recordApi.remove(r.id);
  for (const r of create) await recordApi.create(domainId, r);
}

function PreviewBox({ title, rows, removed }) {
  const t = useT();
  return (
    <div className="preview-box">
      <div className="pl">{title}</div>
      {rows.map((r, i) => (
        <div className="pr" key={i}>
          <span><b>{r.type}</b></span>
          <span>{r.name}</span>
          <span style={{ overflowWrap: 'anywhere' }}>{r.priority != null ? `${r.priority} ` : ''}{r.value || '—'}</span>
        </div>
      ))}
      {removed > 0 && (
        <div className="pl" style={{ marginTop: 8, marginBottom: 0 }}>
          {t(removed === 1 ? 'domainDetail.replaced.one' : 'domainDetail.replaced.other', { n: removed })}
        </div>
      )}
    </div>
  );
}

function TaskModal({ title, description, lang, onClose, onSubmit, canSubmit, cta, children }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      await onSubmit();
    } catch (e2) {
      setErr(e2);
      setBusy(false);
    }
  };
  return (
    <Modal
      as="form"
      onSubmit={submit}
      onClose={onClose}
      maxWidth={580}
      title={title}
      description={description}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>{t('g.cancel')}</button>
          <button type="submit" className="btn-primary" disabled={busy || !canSubmit}>
            {busy ? t('domainDetail.working') : cta}
          </button>
        </>
      }
    >
      <div className="modal-body">
        <InlineError error={err} lang={lang} />
        {children}
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────
// Point to website (task wizard)

function PointToWebsiteModal({ domain, records, lang, t, onClose, onDone }) {
  const [opt, setOpt] = useState('ip');
  const [ip, setIp] = useState('');
  const [host, setHost] = useState('');
  const ipOk = IPV4.test(ip.trim());
  const hostOk = HOSTNAME.test(host.trim());

  const create = opt === 'ip'
    ? [{ type: 'A', name: '@', value: ip.trim(), ttl: 3600 }, { type: 'A', name: 'www', value: ip.trim(), ttl: 3600 }]
    : [{ type: 'CNAME', name: 'www', value: host.trim(), ttl: 3600 }];
  const names = opt === 'ip' ? ['@', 'www'] : ['www'];
  const remove = records.filter((r) => names.includes(r.name) && ['A', 'AAAA', 'CNAME'].includes(recType(r)));

  return (
    <TaskModal
      lang={lang}
      title={t('tw.title', { domain: domain.domain_name })}
      description={t('domainDetail.website.desc')}
      cta={t('g.apply')}
      canSubmit={opt === 'ip' ? ipOk : hostOk}
      onClose={onClose}
      onSubmit={async () => {
        await replaceRecords(domain.id, remove, create);
        toast(t('domainDetail.recordsApplied'));
        onDone();
        onClose();
      }}
    >
      <div role="radiogroup" aria-label={t('domainDetail.website.where')}>
        {[
          ['ip', t('domainDetail.website.ip'), t('domainDetail.website.ip.sub')],
          ['host', t('domainDetail.website.host'), t('domainDetail.website.host.sub')],
        ].map(([k, l, s]) => (
          <div key={k} className="opt" role="radio" tabIndex={0} aria-checked={opt === k} onClick={() => setOpt(k)} onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setOpt(k); } }}>
            <span className="radio" />
            <span>
              <div className="lbl">{l}</div>
              <div className="sub">{s}</div>
            </span>
          </div>
        ))}
      </div>
      {opt === 'ip' ? (
        <FormField label={t('domainDetail.ipAddress')} error={ip && !ipOk ? t('domainDetail.err.ipv4') : ''}>
          <input value={ip} onChange={(e) => setIp(e.target.value)} className="mono" inputMode="decimal" autoComplete="off" />
        </FormField>
      ) : (
        <FormField label={t('domainDetail.hostname')} error={host && !hostOk ? t('domainDetail.err.hostname') : ''}>
          <input value={host} onChange={(e) => setHost(e.target.value)} className="mono" autoComplete="off" spellCheck={false} />
        </FormField>
      )}
      <PreviewBox title={t('domainDetail.preview.set')} rows={create} removed={remove.length} />
    </TaskModal>
  );
}

// ─────────────────────────────────────────────────────────────
// Mail preset (task wizard)

function MailPresetModal({ domain, records, lang, onClose, onDone }) {
  const t = useT();
  const mailDomain = useMailDomain(domain.domain_name);
  const { data: instructions } = useAsync(
    () => (mailDomain ? get(`/emails/domains/${mailDomain.id}/dns-instructions`).catch(() => null) : Promise.resolve(null)),
    [mailDomain?.id],
  );
  const ourMx = (instructions?.records || []).filter((r) => String(r.type).toUpperCase() === 'MX');
  const [provider, setProvider] = useState(null);
  const [customHost, setCustomHost] = useState('');
  const [customPrio, setCustomPrio] = useState('10');

  const providers = [
    ourMx.length > 0 && {
      id: 'ours',
      mark: 'D',
      name: instructions?.mail_server || t('domainDetail.mail.ours'),
      desc: t('domainDetail.mail.ours.desc', { n: mailDomain.account_count }),
      mx: ourMx.map((r) => ({ type: 'MX', name: '@', value: r.value, priority: r.priority ?? 10, ttl: r.ttl || 3600 })),
    },
    { id: 'google', mark: 'G', name: 'Google Workspace', desc: t('domainDetail.mail.google.desc'),
      mx: [{ type: 'MX', name: '@', value: 'smtp.google.com', priority: 1, ttl: 3600 }] },
    { id: 'ms', mark: 'M', name: 'Microsoft 365', desc: t('domainDetail.mail.ms.desc'),
      mx: [{ type: 'MX', name: '@', value: `${domain.domain_name.replaceAll('.', '-')}.mail.protection.outlook.com`, priority: 0, ttl: 3600 }] },
    { id: 'custom', mark: '·', name: t('domainDetail.mail.custom'), desc: t('domainDetail.mail.custom.desc'),
      mx: [{ type: 'MX', name: '@', value: customHost.trim(), priority: Number(customPrio) || 10, ttl: 3600 }] },
    { id: 'none', mark: '—', name: t('domainDetail.mail.none'), desc: t('domainDetail.mail.none.desc'), mx: [] },
  ].filter(Boolean);

  const selected = providers.find((p) => p.id === provider) || null;
  const existingMx = records.filter((r) => recType(r) === 'MX' && r.name === '@');
  const customOk = provider !== 'custom' || HOSTNAME.test(customHost.trim());

  return (
    <TaskModal
      lang={lang}
      title={t('domainDetail.task.mail.title')}
      description={t('domainDetail.mail.desc')}
      cta={t('domainDetail.mail.cta')}
      canSubmit={!!selected && customOk}
      onClose={onClose}
      onSubmit={async () => {
        await replaceRecords(domain.id, existingMx, selected.mx);
        toast(t('domainDetail.mail.applied'));
        onDone();
        onClose();
      }}
    >
      <div role="radiogroup" aria-label={t('domainDetail.mail.provider')}>
        {providers.map((p) => (
          <div key={p.id} className="preset-row" role="radio" tabIndex={0} aria-checked={provider === p.id}
            onClick={() => setProvider(p.id)}
            onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setProvider(p.id); } }}>
            <span className="pic">{p.mark}</span>
            <span>
              <div className="pn">{p.name}</div>
              <div className="pd">{p.desc}</div>
            </span>
            <span className="radio" />
          </div>
        ))}
      </div>
      {provider === 'custom' && (
        <div className="field-row" style={{ marginTop: 12 }}>
          <FormField label={t('domainDetail.mail.server')} error={customHost && !customOk ? t('domainDetail.err.hostname') : ''}>
            <input value={customHost} onChange={(e) => setCustomHost(e.target.value)} className="mono" autoComplete="off" />
          </FormField>
          <FormField label={t('rec.prio')}>
            <input type="number" min="0" value={customPrio} onChange={(e) => setCustomPrio(e.target.value)} />
          </FormField>
        </div>
      )}
      {selected && (
        <PreviewBox
          title={selected.mx.length ? t('domainDetail.preview.set') : t('domainDetail.preview.mxRemoved')}
          rows={selected.mx}
          removed={existingMx.length}
        />
      )}
    </TaskModal>
  );
}

// ─────────────────────────────────────────────────────────────
// Verify (task wizard)

function VerifyModal({ domain, lang, onClose, onDone }) {
  const t = useT();
  const [token, setToken] = useState('');
  const rec = { type: 'TXT', name: '@', value: token.trim(), ttl: 3600 };

  return (
    <TaskModal
      lang={lang}
      title={t('domainDetail.task.verify.title')}
      description={t('domainDetail.verify.desc')}
      cta={t('domainDetail.verify.cta')}
      canSubmit={!!token.trim()}
      onClose={onClose}
      onSubmit={async () => {
        await recordApi.create(domain.id, rec);
        toast(t('domainDetail.verify.created'));
        onDone();
        onClose();
      }}
    >
      <FormField
        label={t('domainDetail.verify.code')}
        hint={t('domainDetail.verify.code.hint')}
      >
        <input value={token} onChange={(e) => setToken(e.target.value)} className="mono" style={{ fontSize: 12.5 }} autoComplete="off" spellCheck={false} />
      </FormField>
      <PreviewBox title={t('domainDetail.preview.create')} rows={[rec]} />
    </TaskModal>
  );
}

// ─────────────────────────────────────────────────────────────
// Redirect (used by the DNS task and the forwarding tab)

function RedirectModal({ domain, lang, onClose, onDone }) {
  const t = useT();
  const [source, setSource] = useState('@');
  const [target, setTarget] = useState('');
  const [code, setCode] = useState('301');
  const [includePath, setIncludePath] = useState(true);
  const srcOk = source.trim() === '@' || /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(source.trim());
  const targetOk = /^https?:\/\/[^\s/$.?#].[^\s]*$/i.test(target.trim());

  return (
    <TaskModal
      lang={lang}
      title={t('domainDetail.task.forward.title')}
      description={t('domainDetail.redirect.desc')}
      cta={t('domainDetail.create')}
      canSubmit={srcOk && targetOk}
      onClose={onClose}
      onSubmit={async () => {
        await redirectApi.create(domain.id, { source: source.trim(), target: target.trim(), code, includePath });
        toast(t('domainDetail.redirect.created'));
        onDone();
        onClose();
      }}
    >
      <FormField
        label={t('domainDetail.redirect.from')}
        hint={t('domainDetail.redirect.from.hint', { domain: domain.domain_name })}
        error={!srcOk ? t('domainDetail.redirect.from.err') : ''}
      >
        <input value={source} onChange={(e) => setSource(e.target.value)} className="mono" autoComplete="off" />
      </FormField>
      <FormField
        label={t('domainDetail.redirect.to')}
        error={target && !targetOk ? t('domainDetail.redirect.to.err') : ''}
      >
        <input type="url" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="https://" className="mono" autoComplete="off" />
      </FormField>
      <div className="field">
        <span className="field-label" id="redirect-type-label">{t('domainDetail.redirect.type')}</span>
        <div className="seg" role="group" aria-labelledby="redirect-type-label">
          <button type="button" aria-pressed={code === '301'} onClick={() => setCode('301')}>{t('domainDetail.redirect.301')}</button>
          <button type="button" aria-pressed={code === '302'} onClick={() => setCode('302')}>{t('domainDetail.redirect.302')}</button>
        </div>
      </div>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--ink-2)' }}>
        <input type="checkbox" checked={includePath} onChange={() => setIncludePath(!includePath)} />
        <span>{t('domainDetail.redirect.keepPath')}</span>
      </label>
    </TaskModal>
  );
}

// ─────────────────────────────────────────────────────────────
// Mail routing tab — derived from the zone's actual records

function MailRoutingTab({ domain, records, lang }) {
  const t = useT();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const list = records.data || [];
  const mx = list.filter((r) => recType(r) === 'MX').sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
  const txt = list.filter((r) => recType(r) === 'TXT');
  const spf = txt.find((r) => r.name === '@' && /^"?v=spf1/i.test(r.value));
  const dmarc = txt.find((r) => r.name === '_dmarc' && /v=DMARC1/i.test(r.value));
  const dkim = list.find((r) => /_domainkey/i.test(r.name));
  const dmarcPolicy = dmarc ? (dmarc.value.match(/p=(\w+)/i) || [])[1] : null;
  const [mailDomain, reloadMailDomain] = useMailDomainState(domain.domain_name);
  const [activating, setActivating] = useState(false);
  const [activateErr, setActivateErr] = useState('');
  const [confirmStop, setConfirmStop] = useState(false);

  const activate = async () => {
    setActivating(true);
    setActivateErr('');
    try {
      await activateMail(domain.domain_name, domain.hosting_id);
      toast(t('mailDomain.activated', { domain: domain.domain_name }));
      reloadMailDomain();
      // The mail records (MX, SPF, DKIM, DMARC) are written right after activation.
      setTimeout(records.reload, 4000);
    } catch (e) {
      setActivateErr(activateMailError(e, lang));
    } finally {
      setActivating(false);
    }
  };

  const provider = (() => {
    const v = (mx[0]?.value || '').toLowerCase();
    if (!v) return null;
    if (v.includes('google')) return 'Google Workspace';
    if (v.includes('outlook')) return 'Microsoft 365';
    return mx[0].value;
  })();

  return (
    <div className="det-grid">
      <section className="card">
        <div className="card-h">
          <h3>{t('domainDetail.routing.title')}</h3>
          <button className="btn-ghost" onClick={() => setOpen(true)}>{t('domainDetail.routing.change')}</button>
        </div>
        {records.loading && <div style={{ padding: '14px 22px', color: 'var(--ink-3)', fontSize: 13 }}>{t('common.loading')}</div>}
        {records.error && <div style={{ padding: '0 22px 14px' }}><InlineError error={records.error} lang={lang} /></div>}
        {!records.loading && !records.error && (
          <div className="mail-current">
            <div className="ic"><Icon name="mail" size={20} /></div>
            <div style={{ minWidth: 0 }}>
              <div className="nm" style={{ overflowWrap: 'anywhere' }}>{provider || t('domainDetail.routing.none')}</div>
              <div className="sub">
                {mx.length
                  ? mx.map((r) => `${r.priority ?? ''} ${r.value}`.trim()).join(' · ')
                  : t('domainDetail.routing.noMx')}
              </div>
            </div>
            {mailDomain && (
              <button className="btn-ghost" onClick={() => navigate('/e-mail')}>{t('svc.mailboxes')} →</button>
            )}
          </div>
        )}
        {mailDomain && (
          <div style={{ padding: '0 22px 18px', borderTop: '1px solid var(--border)' }}>
            <p style={{ margin: '14px 0 10px', fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>
              {t('mailDomain.stopHint')}
            </p>
            <button className="btn-ghost" onClick={() => setConfirmStop(true)}>{t('mailDomain.stop')}</button>
          </div>
        )}
        {confirmStop && mailDomain && (
          <ConfirmDialog
            lang={lang}
            title={t('mailDomain.stopTitle', { domain: domain.domain_name })}
            body={t('mailDomain.stopBody', { n: mailDomain.account_count })}
            cta={t('mailDomain.stop')}
            onClose={() => setConfirmStop(false)}
            onConfirm={async () => {
              await del(`/emails/domains/${mailDomain.id}`);
              setConfirmStop(false);
              toast(t('mailDomain.stopped', { domain: domain.domain_name }));
              reloadMailDomain();
              records.reload();
            }}
          />
        )}
        {!mailDomain && (
          <div style={{ padding: '0 22px 18px', borderTop: '1px solid var(--border)' }}>
            <p style={{ margin: '14px 0 10px', fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>
              {t('mailDomain.routingHint')}
            </p>
            {activateErr && <div className="login-error" role="alert" style={{ marginBottom: 10 }}>{activateErr}</div>}
            <button className="btn-primary" onClick={activate} disabled={activating}>
              {activating ? t('domainDetail.working') : t('mailDomain.activate')}
            </button>
          </div>
        )}
      </section>

      <section className="card">
        <div className="card-h"><h3>{t('domainDetail.deliverability')}</h3></div>
        <div style={{ padding: '10px 22px 18px' }}>
          <DeliverabilityItem
            label="SPF"
            ok={!!spf}
            detail={spf ? spf.value : t('domainDetail.spf.missing')}
          />
          <DeliverabilityItem
            label="DKIM"
            ok={!!dkim}
            detail={dkim ? `${dkim.name}` : t('domainDetail.dkim.missing')}
          />
          <DeliverabilityItem
            label="DMARC"
            ok={!!dmarc && dmarcPolicy && dmarcPolicy.toLowerCase() !== 'none'}
            detail={dmarc
              ? t('domainDetail.dmarc.policy', { policy: dmarcPolicy || '?' })
              : t('domainDetail.dmarc.missing')}
          />
        </div>
      </section>

      {open && <MailPresetModal domain={domain} records={list} lang={lang} onClose={() => setOpen(false)} onDone={records.reload} />}
    </div>
  );
}

function DeliverabilityItem({ label, ok, detail }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '22px minmax(0,1fr)', gap: 10, padding: '10px 0', borderTop: '1px solid var(--border)' }}>
      <span
        style={{
          width: 22, height: 22, borderRadius: 6,
          background: ok ? 'var(--ok-soft)' : 'var(--warn-soft)',
          color: ok ? 'var(--ok)' : 'var(--warn)',
          display: 'grid', placeItems: 'center',
        }}
        aria-hidden="true"
      >
        <Icon name={ok ? 'check' : 'alert'} size={13} stroke={ok ? 2.4 : 1.8} />
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{label}</div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2, overflowWrap: 'anywhere' }}>{detail}</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Forwarding tab — /dns/domains/{id}/redirects

function ForwardingTab({ domain, lang }) {
  const t = useT();
  const { data, loading, error, reload } = useAsync(() => get(`/dns/domains/${domain.id}/redirects`), [domain.id]);
  const [addOpen, setAddOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const list = data || [];
  const fromLabel = (r) => (r.source_subdomain === '@' || !r.source_subdomain ? domain.domain_name : `${r.source_subdomain}.${domain.domain_name}`);

  return (
    <section className="card" style={{ padding: 0 }}>
      <div className="card-h">
        <h3>{t('domainDetail.forwards')}</h3>
        <button className="btn-ghost" onClick={() => setAddOpen(true)}>
          <Icon name="plus" size={13} stroke={2} />
          <span style={{ marginLeft: 4 }}>{t('domainDetail.add')}</span>
        </button>
      </div>
      {loading && <div style={{ padding: '14px 22px', color: 'var(--ink-3)', fontSize: 13 }}>{t('common.loading')}</div>}
      {error && <div style={{ padding: '0 22px 14px' }}><InlineError error={error} lang={lang} /></div>}
      {!loading && !error && list.length === 0 && (
        <div className="empty-state">
          <div className="iccirc"><Icon name="arrow-right" size={22} /></div>
          <h4>{t('domainDetail.forwards.empty')}</h4>
          <p>{t('domainDetail.forwards.empty.sub')}</p>
        </div>
      )}
      {list.map((f) => (
        <div className="fw-row" key={f.id}>
          <span className="from" title={fromLabel(f)}>{fromLabel(f)}</span>
          <span className="ar"><Icon name="arrow-right" size={14} /></span>
          <span className="to" title={f.target_url}>{f.target_url}</span>
          <span className="typ">
            {t(String(f.redirect_type) === '302' ? 'domainDetail.forward.temporary' : String(f.redirect_type) === 'frame' ? 'domainDetail.forward.frame' : 'domainDetail.forward.permanent')}
            <span style={{ color: 'var(--ink-4)', marginLeft: 6 }}>{String(f.redirect_type) === 'frame' ? '' : f.redirect_type}</span>
          </span>
          <span className="acts">
            <button className="icon-btn" onClick={() => setToDelete(f)} title={t('domainDetail.delete')} aria-label={t('domainDetail.forward.delete', { from: fromLabel(f) })}>
              <Icon name="trash" size={14} />
            </button>
          </span>
        </div>
      ))}

      {addOpen && <RedirectModal domain={domain} lang={lang} onClose={() => setAddOpen(false)} onDone={reload} />}
      {toDelete && (
        <ConfirmDialog
          lang={lang}
          title={t('domainDetail.forward.delete.title')}
          body={`${fromLabel(toDelete)} → ${toDelete.target_url}`}
          cta={t('domainDetail.delete')}
          onClose={() => setToDelete(null)}
          onConfirm={async () => {
            await redirectApi.remove(toDelete.id);
            setToDelete(null);
            toast(t('domainDetail.forward.deleted'));
            reload();
          }}
        />
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Settings tab — nameservers + DNSSEC

// GET /dns/domains/{id}/nameservers: the choice ("default" = ours, resolved package → global;
// "own" = the customer's) and the set it resolves to.
function useNameserverView(domainId) {
  return useAsync(() => get(`/dns/domains/${domainId}/nameservers`), [domainId]);
}

function SettingsTab({ domain, lang, reload }) {
  const t = useT();
  const view = useNameserverView(domain.id);
  const v = view.data;
  const [mode, setMode] = useState(null);
  const [ns, setNs] = useState(['', '', '', '']);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!v) return;
    setMode(v.mode);
    const base = v.mode === 'own' ? [...v.effective] : [];
    while (base.length < 4) base.push('');
    setNs(base);
  }, [v]);

  const filled = ns.map((x) => x.trim()).filter(Boolean);
  const invalid = filled.some((x) => !HOSTNAME.test(x) || !x.includes('.'));
  const ownReady = filled.length >= 2 && !invalid;
  const changed = v && (mode !== v.mode || (mode === 'own' && filled.join(',') !== v.effective.join(',')));

  const save = async (e) => {
    e.preventDefault();
    if (mode === 'own' && !ownReady) return;
    setBusy(true);
    setErr(null);
    try {
      await patch(`/dns/domains/${domain.id}/nameservers`, { mode, nameservers: mode === 'own' ? filled : [] });
      toast(t('domainDetail.ns.saved'));
      view.reload();
      reload?.();
    } catch (e2) {
      setErr(e2);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <section className="card">
        <div className="card-h"><h3>{t('domainDetail.nameservers')}</h3></div>
        <form style={{ padding: '12px 22px 18px' }} onSubmit={save}>
          <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 12, lineHeight: 1.5, maxWidth: '60ch' }}>
            {t('domainDetail.ns.desc')}
          </div>
          {view.loading && <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{t('common.loading')}</div>}
          <InlineError error={view.error || err} lang={lang} />
          {mode && (
            <>
              <div className="seg seg--sm" role="group" aria-label={t('domainDetail.nameservers')} style={{ marginBottom: 14 }}>
                <button type="button" aria-pressed={mode === 'default'} onClick={() => setMode('default')}>{t('domainDetail.ns.modeDefault')}</button>
                <button type="button" aria-pressed={mode === 'own'} onClick={() => setMode('own')}>{t('domainDetail.ns.modeOwn')}</button>
              </div>

              {mode === 'default' ? (
                <EffectiveNameservers view={v} pending={v.mode !== 'default'} />
              ) : (
                <>
                  <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 12, lineHeight: 1.5, maxWidth: '60ch' }}>
                    {t('domainDetail.ns.ownDesc')}
                  </div>
                  {ns.map((x, i) => (
                    <FormField key={i} label={t('domainDetail.ns.label', { n: i + 1 })}>
                      <input
                        value={x}
                        onChange={(e) => {
                          const next = [...ns];
                          next[i] = e.target.value;
                          setNs(next);
                        }}
                        className="mono"
                        autoComplete="off"
                        spellCheck={false}
                      />
                    </FormField>
                  ))}
                  {invalid && <p className="field-error">{t('domainDetail.ns.invalid')}</p>}
                </>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                <button className="btn-primary" type="submit" disabled={busy || !changed || (mode === 'own' && !ownReady)}>
                  {busy ? t('domainDetail.working') : t('g.save')}
                </button>
              </div>
            </>
          )}
        </form>
      </section>

      <DnssecCard domain={domain} lang={lang} />
    </div>
  );
}

// The set our nameservers resolve to, where it comes from and whether the registrar has it.
function EffectiveNameservers({ view, pending }) {
  const t = useT();
  if (!view) return null;
  if (pending) {
    return <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{t('domainDetail.ns.defaultExplain')}</div>;
  }
  const state = view.in_sync === null ? 'other' : view.in_sync ? 'ok' : 'pending';
  return (
    <div>
      {view.effective.length ? (
        <div className="mono" style={{ fontSize: 13, lineHeight: 1.7 }}>
          {view.effective.map((n) => <div key={n}>{n}</div>)}
        </div>
      ) : (
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{t('domainDetail.ns.registrarPicks')}</div>
      )}
      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8 }}>
        {t(`domainDetail.ns.source.${view.source}`)}
      </div>
      <div style={{ marginTop: 10 }}>
        <span className={'status-pill' + (state === 'pending' ? ' is-warn' : state === 'other' ? ' is-paused' : '')}>
          <span className="dot" aria-hidden="true" />
          {t(`domainDetail.ns.state.${state}`)}
        </span>
      </div>
    </div>
  );
}

function DnssecCard({ domain, lang }) {
  const t = useT();
  const { data, loading, error, reload } = useAsync(() => get(`/dns/domains/${domain.id}/dnssec`), [domain.id]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [enabled, setEnabled] = useState(null);

  useEffect(() => {
    if (data) setEnabled(!!data.dnssec_enabled);
  }, [data]);

  const set = async (v) => {
    setBusy(true);
    setErr(null);
    try {
      const state = await patch(`/dns/domains/${domain.id}/dnssec`, { enabled: v });
      toast(t(v ? (state?.dnssec_published || state?.dns_host !== 'bunny' ? 'domainDetail.dnssec.enabled' : 'domainDetail.dnssec.enabledPending')
        : 'domainDetail.dnssec.disabled'));
      reload();
    } catch (e) {
      setErr(e);
      reload();
    } finally {
      setBusy(false);
    }
  };

  // Our zone (Bunny) can be switched; a legacy Nextname zone is always signed; own nameservers
  // mean the customer's DNS provider signs.
  const host = data?.dns_host || 'nextname';
  const switchable = host === 'bunny';
  const dsRecord = data?.ds_records?.[0]?.ds_record;
  const status = !data?.dnssec_enabled ? 'off'
    : host !== 'bunny' || data.dnssec_published ? 'on'
      : data.registrar_managed ? 'pending' : 'copyDs';

  return (
    <section className="card">
      <div className="card-h"><h3>DNSSEC</h3></div>
      <div style={{ padding: '12px 22px 18px' }}>
        <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 14, lineHeight: 1.5, maxWidth: '56ch' }}>
          {t('domainDetail.dnssec.desc')}
        </div>
        {loading && <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{t('common.loading')}</div>}
        <InlineError error={error || err} lang={lang} />
        {enabled !== null && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: 'var(--ink-3)' }} id={`dnssec-${domain.id}`}>{t('domainDetail.status.active')}</span>
              {switchable ? (
                <div className="seg seg--sm" role="group" aria-labelledby={`dnssec-${domain.id}`}>
                  <button type="button" disabled={busy} aria-pressed={enabled === true} onClick={() => !enabled && set(true)}>{t('common.yes')}</button>
                  <button type="button" disabled={busy} aria-pressed={enabled === false} onClick={() => enabled && set(false)}>{t('common.no')}</button>
                </div>
              ) : (
                <span className={'status-pill' + (enabled ? '' : ' is-paused')}>
                  <span className="dot" aria-hidden="true" />
                  {t(enabled ? 'domainDetail.on' : 'domainDetail.off')}
                </span>
              )}
              {data?.dnssec_updated_at && (
                <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>
                  {t('domainDetail.dnssec.changed', { date: formatDate(data.dnssec_updated_at, lang) })}
                </span>
              )}
            </div>
            <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5, maxWidth: '60ch' }}>
              {switchable ? t(`domainDetail.dnssec.state.${status}`)
                : t(host === 'nextname' ? 'domainDetail.dnssec.legacyZone' : 'domainDetail.dnssec.ownNameservers')}
            </div>
            {switchable && status === 'copyDs' && dsRecord && (
              <code className="mono" style={{ display: 'block', marginTop: 8, fontSize: 12, wordBreak: 'break-all', background: 'var(--surface-2)', padding: '8px 10px', borderRadius: 8 }}>
                {dsRecord}
              </code>
            )}
          </>
        )}
      </div>
    </section>
  );
}

